'use client'

/**
 * The React side of the app's themes (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md,
 * "Ruling 3" and "Ruling 6"): read the store, keep it in step with the account, save a choice, and give
 * the app sidebar its variant.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchApi } from '@/lib/api/client'
import { useSidebarVariant } from '@/hooks/use-theme-settings'
import { readPersistedWorkspaceId } from '@/lib/tenant/workspace-id-storage'
import { useWorkspaceStore } from '@/stores/workspace-store'
import { isAppThemeChoice, type AppThemeChoice } from './app-themes'
import { appThemeName } from './app-theme-words'
import { useShellTranslation } from '@/lib/shell/use-shell-translation'
import { appSidebarVariant, type AppSidebarVariant } from './app-builder'
import {
  adoptAccountAppTheme,
  appThemeSaveEpoch,
  beginAppThemeSave,
  endAppThemeSave,
  getAppThemeSnapshot,
  getEffectiveBrandSnapshot,
  getSavedAppTheme,
  getServerAppThemeSnapshot,
  setAppTheme,
  subscribeAppTheme,
  type AppThemeSnapshot,
} from './app-theme-store'
import type { EffectiveBrandTheme } from './brand-theme'

import { isViewingAsAnotherUser } from '@/lib/auth/viewing-as-another-user'

export const APP_THEME_PATH = '/users/me/preferences/app-theme'

/** One cache entry per signed-in user. */
export const appThemeQueryKey = (userId: string | null) => ['users', 'me', 'app-theme', userId] as const

/** Is a super-admin viewing the app as this user? (lib/auth/viewing-as-another-user.ts; kept here for its importers.) */
export { isViewingAsAnotherUser }

/** The account's answer as a choice; null for none (never chosen) and for anything outside the catalogue. */
const themeFromAnswer = (answer: unknown): AppThemeChoice | null => {
  const theme = answer && typeof answer === 'object' ? (answer as { theme?: unknown }).theme : undefined
  return isAppThemeChoice(theme) ? theme : null
}

/** The theme choice as the app sees it right now (no choice, and no app page, on the server). */
export function useAppThemeState(): AppThemeSnapshot {
  return useSyncExternalStore(subscribeAppTheme, getAppThemeSnapshot, getServerAppThemeSnapshot)
}

/** What the server last said for this page; null until it has. The menu reads it here: the read itself is the scope's. */
export function useBrandThemeState(): EffectiveBrandTheme | null {
  return useSyncExternalStore(subscribeAppTheme, getEffectiveBrandSnapshot, () => null)
}

/**
 * The workspace a theme save and the brand read go out under: the one lib/api/client.ts names in `x-workspace-id`. The
 * all-workspaces view is no workspace, but the client still sends the last one, and the server answers for that.
 * Null is someone under the app sidebar with no workspace at all (the switcher's "no workspaces", or before it has
 * resolved them): PUT app-theme and PUT icon-set answer them a 400, so the menu closes its rows and says why.
 */
export function useThemeWorkspaceId(): string | null {
  const active = useWorkspaceStore((s) => s.activeWorkspaceId)
  return active && active !== 'all' ? active : readPersistedWorkspaceId()
}

/** Why the API refused a theme or an icon set a person read about. Null: something else went wrong. */
export type ThemeRefusal = 'no-workspace' | null

export function themeRefusal(error: unknown): ThemeRefusal {
  const { statusCode, message } = (error ?? {}) as { statusCode?: unknown; message?: unknown }
  // TenantContextGuard's refusal carries no errorCode: the header it names is the only sign of it.
  return statusCode === 400 && typeof message === 'string' && message.includes('x-workspace-id') ? 'no-workspace' : null
}

/** The sentence under "wasn't saved" for a refusal; null for one the caller words itself. */
export const themeRefusalKey = (refusal: ThemeRefusal): string | null =>
  refusal === 'no-workspace' ? 'shell.theme.app.noWorkspace' : null

/**
 * The app sidebar's variant for the theme on screen (a preview included): the builder's under Custom
 * and for someone who never chose, docked on Classic, and a ready-made theme's own — Harbor floats.
 */
export function useAppSidebarVariant(): AppSidebarVariant {
  const builder = useSidebarVariant()
  const { shown } = useAppThemeState()
  return appSidebarVariant(shown, builder)
}

interface AccountAnswer {
  theme: AppThemeChoice | null
  /** The save epoch when the question was asked (app-theme-store.ts). */
  askedAt: number
}

/**
 * Ask the account for the signed-in user's choice (once per user; again only when stale and the tab
 * regains focus) and mirror the answer — unless a save overtook it. With a workspace, the brand read is on its way too, and
 * the store only records the answer until that read lands (app-theme-store.ts).
 */
export function useAppThemeAccountSync(userId: string | null): void {
  const readComing = useThemeWorkspaceId() !== null
  const { data } = useQuery({
    queryKey: appThemeQueryKey(userId),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<AccountAnswer> => {
      const askedAt = appThemeSaveEpoch()
      const answer = await fetchApi<{ theme?: unknown }>(APP_THEME_PATH)
      return { theme: themeFromAnswer(answer), askedAt }
    },
  })

  useEffect(() => {
    if (data && userId) adoptAccountAppTheme(data.theme, userId, data.askedAt, readComing)
  }, [data, userId, readComing])
}

// A run of saves shares one rollback point: the last choice the account confirmed, taken when the run
// starts. Only the latest save's outcome counts — one that fails after a later one was made changes
// nothing. Module-level, because the menu that saves unmounts when its popover closes.
let latestSave = 0
let savesRunning = 0
let confirmed: { theme: AppThemeChoice | null } | null = null

/**
 * Save a choice: <html> and the cookie change at once — before the API answers — and go back, with a
 * toast, if the save fails. While a super-admin views the app as a user, the choice applies to the
 * page only: it is written neither to that user's account nor to this browser's cookie.
 *
 * A refusal says why in the interface language. What goes back is the person's own choice, not what is painted.
 */
export function useSaveAppTheme(userId: string | null): (next: AppThemeChoice) => Promise<boolean> {
  const queryClient = useQueryClient()
  // The toast speaks the interface language, as the menu does.
  const { t } = useShellTranslation()
  return useCallback(
    async (next: AppThemeChoice): Promise<boolean> => {
      if (!isAppThemeChoice(next)) return false
      const current = getSavedAppTheme()
      if (isViewingAsAnotherUser()) {
        // No owner: the page shows it, no cookie keeps it (writeAppThemeCookie clears one it cannot own).
        setAppTheme(next, null)
        return true
      }
      const mine = ++latestSave
      if (savesRunning === 0) confirmed = { theme: current }
      savesRunning += 1
      beginAppThemeSave()
      setAppTheme(next, userId)
      try {
        const answer = await fetchApi<{ theme?: unknown }>(APP_THEME_PATH, {
          method: 'PUT',
          body: JSON.stringify({ theme: next }),
        })
        const saved = themeFromAnswer(answer) ?? next
        if (mine === latestSave) {
          confirmed = { theme: saved }
          if (saved !== next) setAppTheme(saved, userId)
          queryClient.setQueryData<AccountAnswer>(appThemeQueryKey(userId), { theme: saved, askedAt: -1 })
        }
        return true
      } catch (error) {
        if (mine === latestSave) {
          const back = confirmed ? confirmed.theme : current
          setAppTheme(back, userId)
          const because = themeRefusalKey(themeRefusal(error))
          toast.error(t('shell.theme.app.notSaved'), {
            description: because
              ? t(because)
              : back
                ? t('shell.theme.app.backTo', { theme: appThemeName(back, t) })
                : t('shell.theme.app.backAsItWas'),
          })
        }
        return false
      } finally {
        savesRunning -= 1
        endAppThemeSave()
      }
    },
    [queryClient, userId, t],
  )
}

/** Tests only. */
export function resetAppThemeSavesForTests(): void {
  latestSave = 0
  savesRunning = 0
  confirmed = null
}
